import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Camera as CameraIcon, Zap, RefreshCcw, Coins, Crown, ImageIcon, Heart } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { MODES, MODE_MAP } from "@/lib/modes";
import ScanResultSheet from "@/components/ScanResultSheet";
import TranslationOverlay from "@/components/TranslationOverlay";
import TipJar from "@/components/TipJar";
import { useNavigate, Link } from "react-router-dom";

// Sample fallback images (used if camera not available)
const SAMPLE_IMAGES = {
  jixxeo_lens:  "https://images.unsplash.com/photo-1525498128493-380d1990a112?w=800&q=80",
  plant_doctor: "https://images.unsplash.com/photo-1491147334573-44cbb4602074?w=800&q=80",
  animal_id:    "https://images.unsplash.com/photo-1544985361-b420d7a77043?w=800&q=80",
  food_scanner: "https://images.unsplash.com/photo-1600663791817-d74f5196ba29?w=800&q=80",
  translator_vision: "https://images.unsplash.com/photo-1526304640581-d334cdbbf45e?w=800&q=80",
  ai_portrait:  "https://images.unsplash.com/photo-1544005313-94ddf0286df2?w=800&q=80",
  meal_maker:   "https://images.unsplash.com/photo-1414235077428-338989a2e8c0?w=800&q=80",
  repair_mode:  "https://images.unsplash.com/photo-1581092160562-40aa08e78837?w=800&q=80",
};

async function urlToBase64(url) {
  const res = await fetch(url);
  const blob = await res.blob();
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onerror = reject;
    r.onload = () => resolve(String(r.result));
    r.readAsDataURL(blob);
  });
}

export default function ScanPage() {
  const { user, refresh } = useAuth();
  const nav = useNavigate();
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);
  const fileInputRef = useRef(null);
  const [mode, setMode] = useState("jixxeo_lens");
  const [facing, setFacing] = useState("environment");
  const [cameraOk, setCameraOk] = useState(false);
  const [cameraError, setCameraError] = useState("");
  const [requesting, setRequesting] = useState(false);
  const [scanning, setScanning] = useState(false);
  const [result, setResult] = useState(null);
  const [overlayScan, setOverlayScan] = useState(null);
  const [tipOpen, setTipOpen] = useState(false);

  useEffect(() => { startCamera(); return () => stopCamera(); /* eslint-disable-next-line */ }, [facing]);

  async function startCamera() {
    stopCamera();
    setCameraError("");
    if (!window.isSecureContext) { setCameraOk(false); setCameraError("Camera requires HTTPS. Open the app via https://"); return; }
    if (!navigator.mediaDevices?.getUserMedia) { setCameraOk(false); setCameraError("Your browser does not support camera access"); return; }
    setRequesting(true);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: { ideal: facing }, width: { ideal: 1280 }, height: { ideal: 720 } },
        audio: false,
      });
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        await videoRef.current.play().catch(() => null);
      }
      setCameraOk(true);
    } catch (e) {
      setCameraOk(false);
      const name = e?.name || "";
      if (name === "NotAllowedError" || name === "SecurityError") {
        setCameraError("Camera permission denied. Tap the lock icon in your address bar → Site settings → allow Camera, then tap Enable.");
      } else if (name === "NotFoundError" || name === "OverconstrainedError") {
        setCameraError("No camera found on this device.");
      } else if (name === "NotReadableError") {
        setCameraError("Camera is in use by another app. Close other camera apps and tap Enable.");
      } else {
        setCameraError("Could not access camera. Tap Enable to try again.");
      }
    } finally {
      setRequesting(false);
    }
  }

  function stopCamera() {
    if (streamRef.current) { streamRef.current.getTracks().forEach((t) => t.stop()); streamRef.current = null; }
  }

  async function captureBase64() {
    if (cameraOk && videoRef.current && videoRef.current.videoWidth > 0) {
      const v = videoRef.current;
      const c = canvasRef.current;
      c.width = 720;
      c.height = Math.floor((720 * v.videoHeight) / v.videoWidth);
      c.getContext("2d").drawImage(v, 0, 0, c.width, c.height);
      return c.toDataURL("image/jpeg", 0.82);
    }
    // Fallback: sample image for the current mode
    return urlToBase64(SAMPLE_IMAGES[mode] || SAMPLE_IMAGES.jixxeo_lens);
  }

  async function onShutter() {
    if (scanning) return;
    if (!user) { nav("/auth"); return; }
    setScanning(true);
    try {
      const dataUrl = await captureBase64();
      const r = await api.post("/scans", { mode, image_base64: dataUrl });
      if (mode === "translator_vision" && (r.data.boxes?.length ?? 0) > 0) {
        setOverlayScan(r.data);
      } else {
        setResult(r.data);
      }
      await refresh();
      toast.success(`+${r.data.xp_earned} XP · ${MODE_MAP[mode].label} scan complete`);
    } catch (e) {
      const msg = e?.response?.data?.detail || "Scan failed";
      toast.error(msg);
      if (e?.response?.status === 402) nav("/coins");
    } finally { setScanning(false); }
  }

  async function onPickFile(e) {
    const f = e.target.files?.[0]; if (!f || !user) return;
    setScanning(true);
    try {
      const dataUrl = await new Promise((res, rej) => {
        const r = new FileReader(); r.onerror = rej; r.onload = () => res(String(r.result)); r.readAsDataURL(f);
      });
      const r = await api.post("/scans", { mode, image_base64: dataUrl });
      setResult(r.data); await refresh();
      toast.success(`+${r.data.xp_earned} XP · Scan complete`);
    } catch (err) { toast.error(err?.response?.data?.detail || "Scan failed"); }
    finally { setScanning(false); e.target.value = ""; }
  }

  async function toggleFav(scan, favorite) {
    try { const r = await api.patch(`/scans/${scan.id}/favorite`, { favorite }); setResult(r.data); }
    catch { toast.error("Update failed"); }
  }

  const currentMode = MODE_MAP[mode];

  return (
    <div className="min-h-screen relative">
      {/* Camera viewfinder */}
      <div className="fixed inset-0 bg-black">
        {cameraOk ? (
          <video ref={videoRef} playsInline muted autoPlay className="w-full h-full object-cover" />
        ) : (
          <div className="w-full h-full jx-grid-bg flex items-center justify-center relative">
            <img src={SAMPLE_IMAGES[mode]} alt="" className="absolute inset-0 w-full h-full object-cover opacity-30" />
            <div className="relative jx-glass rounded-3xl px-5 py-5 text-center max-w-[85%] border border-cyan-400/30">
              <div className="w-14 h-14 rounded-2xl jx-shutter mx-auto flex items-center justify-center">
                <CameraIcon size={24} className="text-slate-950" />
              </div>
              <p className="mt-3 text-xs font-mono-data uppercase tracking-widest text-cyan-300">
                {requesting ? "Requesting permission…" : "Camera is off"}
              </p>
              <p className="mt-1 text-sm text-slate-300 leading-snug">
                {cameraError || "Allow camera access to scan the world around you."}
              </p>
              <button
                data-testid="enable-camera-button"
                onClick={startCamera}
                disabled={requesting}
                className="mt-4 w-full h-11 rounded-xl bg-cyan-400 text-slate-950 font-heading font-bold active:scale-95 disabled:opacity-60"
              >
                {requesting ? "Opening…" : "Enable Camera"}
              </button>
              <button
                data-testid="simulator-skip-button"
                onClick={() => fileInputRef.current?.click()}
                className="mt-2 w-full h-10 rounded-xl border border-white/15 text-slate-300 text-sm active:scale-95"
              >
                Upload a photo instead
              </button>
            </div>
          </div>
        )}
        <canvas ref={canvasRef} className="hidden" />
        {/* HUD */}
        <div className="absolute inset-0 pointer-events-none">
          {/* Corners */}
          <div className="jx-corner border-t-2 border-l-2 rounded-tl-2xl top-16 left-6" />
          <div className="jx-corner border-t-2 border-r-2 rounded-tr-2xl top-16 right-6" />
          <div className="jx-corner border-b-2 border-l-2 rounded-bl-2xl bottom-52 left-6" />
          <div className="jx-corner border-b-2 border-r-2 rounded-br-2xl bottom-52 right-6" />
          {scanning && (
            <div className="absolute inset-x-6 top-16 bottom-52 overflow-hidden rounded-2xl">
              <div className="w-full h-1 bg-cyan-400 shadow-[0_0_18px_rgba(0,229,255,0.9)] jx-scan-sweep" />
            </div>
          )}
        </div>
      </div>

      {/* Top bar */}
      <div className="absolute top-0 left-0 right-0 z-20 flex items-center justify-between p-3 max-w-md mx-auto">
        <Link to="/profile" data-testid="scan-top-profile" className="jx-glass rounded-full h-10 px-3 flex items-center gap-2">
          <div className="w-6 h-6 rounded-full bg-cyan-400 text-slate-900 flex items-center justify-center font-heading font-bold text-xs">
            {user?.username?.[0]?.toUpperCase() || "J"}
          </div>
          <span className="text-xs font-mono-data uppercase tracking-widest">Lv {user?.level || 1}</span>
        </Link>
        <div className="flex gap-2">
          <Link to="/coins" data-testid="coin-balance-display" className="jx-glass rounded-full h-10 px-3 flex items-center gap-1.5">
            <Coins size={14} className="text-amber-300" />
            <span className="font-mono-data text-xs">{user?.coins ?? 0}</span>
          </Link>
          <button onClick={() => setTipOpen(true)} data-testid="scan-tip-cta" className="jx-glass rounded-full h-10 w-10 flex items-center justify-center text-rose-300 active:scale-95" aria-label="Tip">
            <Heart size={14} fill="currentColor" />
          </button>
          <Link to="/coins" data-testid="elite-cta" className={`jx-glass rounded-full h-10 px-3 flex items-center gap-1.5 ${user?.elite ? "text-amber-300" : "text-slate-300"}`}>
            <Crown size={14} /><span className="text-xs font-mono-data uppercase">Elite</span>
          </Link>
        </div>
      </div>

      {/* Mode selector */}
      <div className="absolute left-0 right-0 bottom-40 z-20 max-w-md mx-auto">
        <div className="flex gap-2 overflow-x-auto no-scrollbar px-4 pb-2" data-testid="mode-selector">
          {MODES.map((m) => {
            const active = m.id === mode;
            return (
              <button
                key={m.id}
                data-testid={`mode-${m.id}`}
                onClick={() => setMode(m.id)}
                className={`shrink-0 flex flex-col items-center gap-0.5 px-3 py-2 rounded-2xl border transition-all active:scale-95 ${active ? "bg-white/10 border-white/30" : "bg-black/40 border-white/10"}`}
                style={active ? { boxShadow: `0 0 20px ${m.color}66`, borderColor: `${m.color}88` } : {}}
              >
                <span className="text-[11px] font-heading font-semibold" style={{ color: active ? m.color : "#cbd5e1" }}>{m.label}</span>
                <span className="text-[9px] font-mono-data uppercase tracking-widest text-slate-500">{m.desc}</span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Shutter */}
      <div className="absolute left-0 right-0 bottom-24 z-20 flex items-center justify-center gap-6 max-w-md mx-auto px-8">
        <button data-testid="scan-upload-image" onClick={() => fileInputRef.current?.click()} className="w-12 h-12 rounded-2xl jx-glass flex items-center justify-center active:scale-95">
          <ImageIcon size={20} />
        </button>
        <input ref={fileInputRef} type="file" accept="image/*" onChange={onPickFile} className="hidden" />

        <button
          data-testid="main-shutter-tap-button"
          disabled={scanning}
          onClick={onShutter}
          className={`relative w-[76px] h-[76px] rounded-full jx-shutter jx-pulse flex items-center justify-center active:scale-95 disabled:opacity-70`}
          style={{ background: `radial-gradient(circle at 30% 30%, #7FF3FF 0%, ${currentMode.color} 40%, #0284C7 100%)` }}
        >
          {scanning ? <Zap size={30} className="text-slate-900 animate-pulse" /> : <CameraIcon size={28} className="text-slate-900" />}
          <div className="absolute inset-0 rounded-full border-2 border-white/40" />
        </button>

        <button data-testid="scan-flip-camera" onClick={() => setFacing((f) => (f === "environment" ? "user" : "environment"))}
          className="w-12 h-12 rounded-2xl jx-glass flex items-center justify-center active:scale-95">
          <RefreshCcw size={18} />
        </button>
      </div>

      <p className="absolute bottom-[76px] left-0 right-0 text-center z-20 text-[10px] font-mono-data uppercase tracking-widest text-slate-400 pointer-events-none">
        Tap to scan • {currentMode.label} · Jixxeo AI
      </p>

      {result && <ScanResultSheet result={result} onClose={() => setResult(null)} onToggleFavorite={toggleFav} />}
      {overlayScan && (
        <TranslationOverlay
          scan={overlayScan}
          onClose={() => { const s = overlayScan; setOverlayScan(null); setResult(s); }}
        />
      )}
      <TipJar open={tipOpen} onClose={() => setTipOpen(false)} />
    </div>
  );
}
